import { StructuredText, Image } from 'react-datocms';
import { NavigationCard } from '@/components/NavigationCard';
import type { HomeHeroV3Fragment } from '@/utils/dato/generated';
import { customNodeRules, renderBlock } from '../structuredText';
import { BiscuitNavigationCard } from '@/components/BiscuitNavigationCard';

const BACKGROUND_COLORS: Record<string, string> = {
  'carnation-pink': '#FFB0CB',
  'amber-yellow': '#FFBE3D',
  'dark-teal': '#0E2C27',
  honeydew: '#B5F0DD',
  almond: '#E5D6C3',
  white: '#FFFFFF',
  teal: '#71E6C0',
  mint: '#EFFFF6',
};

const DEFAULT_BG = '#EFFFF6';

const BadgeRow = ({ badges }: { badges: HomeHeroV3Fragment['badgeImages'] }) => (
  <div className="flex items-end gap-3">
    {(badges ?? []).map(
      (badge) =>
        badge.responsiveImage && (
          <img
            className="h-12 w-auto object-contain"
            alt={badge.responsiveImage.alt ?? ''}
            key={badge.responsiveImage.src}
            src={badge.responsiveImage.src}
          />
        ),
    )}
  </div>
);

const HomeHeroV3 = ({
  bottomImageDesktop,
  bottomImageMobile,
  navigationCards,
  additionalImage,
  backgroundColor,
  leftBadgeImages,
  homeHeroCopy,
  badgeImages,
  tagline,
  title,
}: HomeHeroV3Fragment) => {
  const bgColor = backgroundColor ? (BACKGROUND_COLORS[backgroundColor] ?? DEFAULT_BG) : DEFAULT_BG;
  const hasBadges = (leftBadgeImages ?? []).length > 0 || (badgeImages ?? []).length > 0;

  return (
    <>
      <div style={{ backgroundColor: bgColor }} className="pt-10 text-center">
        <div className="container px-5 pb-13 md:pb-8">
          <h1 className="text-[clamp(2rem,7vw,4.75rem)] sm:text-[76px] lg:text-8xl leading-[.95] mb-6 max-w-3xl mx-auto">
            {(title ?? '').split('\n').map((line, i) =>
              i === 0 ? (
                line
              ) : (
                <span className="block mt-3" key={i}>
                  {line}
                </span>
              ),
            )}
            {tagline ? <span className="block mt-3">{tagline}</span> : null}
          </h1>
          <div className="max-w-2xl mx-auto [&_.btn]:inline-block [&_.button]:btn-lg [&_p]:text-[17px] [&_p]:leading-[1.2] [&_p]:font-normal">
            <StructuredText customNodeRules={customNodeRules} renderBlock={renderBlock} data={homeHeroCopy} />
          </div>
        </div>

        {bottomImageMobile?.responsiveImage ? (
          <div className="md:hidden pb-12 -mt-9">
            <div className="relative h-[400px] overflow-hidden">
              <Image data={bottomImageMobile.responsiveImage} objectPosition="top" objectFit="cover" layout="fill" />
              <div
                style={{ background: `linear-gradient(to bottom, transparent 0%, ${bgColor} 80%)` }}
                className="absolute bottom-0 inset-x-0 h-8 z-10 pointer-events-none"
              />
            </div>
            {hasBadges ? (
              <div className="flex w-full justify-evenly items-end px-3 mt-5">
                {[...(leftBadgeImages ?? []), ...(badgeImages ?? [])].map(
                  (badge) =>
                    badge.responsiveImage && (
                      <img
                        className="h-12 w-auto object-contain"
                        alt={badge.responsiveImage.alt ?? ''}
                        key={badge.responsiveImage.src}
                        src={badge.responsiveImage.src}
                      />
                    ),
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {bottomImageDesktop?.responsiveImage ? (
          <div className="hidden md:block relative pb-12">
            <div className="relative">
              <Image
                data={bottomImageDesktop.responsiveImage}
                pictureClassName="w-full"
                className="w-full block"
                sizes="(max-width: 2000px) 100vw, 2000px"
                layout="responsive"
              />
              <div
                style={{ background: `linear-gradient(to bottom, transparent 0%, ${bgColor} 65%)` }}
                className="absolute bottom-0 inset-x-0 h-10 z-10 pointer-events-none"
              />
              {hasBadges ? (
                <div className="absolute bottom-14 inset-x-0 px-5 flex justify-between items-end z-20">
                  <BadgeRow badges={leftBadgeImages} />
                  <BadgeRow badges={badgeImages} />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {navigationCards.length > 0 ? (
        <div className="container px-5 mb-9 -mt-10">
          <div className="bg-brand py-9 px-5 lg:px-9 lg:py-13 rounded-xl -mt-9 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {navigationCards.map((card) =>
              card.__typename === 'BiscuitNavigationCardRecord' ? (
                <BiscuitNavigationCard key={card.id} {...card} />
              ) : (
                <NavigationCard key={card.id} {...card} />
              ),
            )}
          </div>
        </div>
      ) : null}

      {additionalImage?.responsiveImage ? (
        <div className="container px-5 mb-9">
          <Image data={additionalImage.responsiveImage} className="w-full rounded-xl" />
        </div>
      ) : null}
    </>
  );
};

export default HomeHeroV3;
